/** @format */

import React from "react";
import {
	Box,
	Container,
	Typography,
	Grid,
	Card,
	CardContent,
	Button,
	styled,
	Paper,
} from "@mui/material";
import ForumIcon from "@mui/icons-material/Forum";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SchoolIcon from "@mui/icons-material/School";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import CelebrationIcon from "@mui/icons-material/Celebration";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import {
	ArrowForward,
	PersonAdd,
	Groups,
	Share,
	Stars,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { colors } from "../../utility/types/constant";
import { CTAButtonsWrapper, PrimaryCTAButton } from "../banner-one/styles";

const HeroSection = styled(Box)(({ theme }) => ({
	background: colors.primary,
	color: "#fff",
	padding: "80px 0",
	textAlign: "center",
	fontFamily: "'Montserrat', sans-serif",
}));

const FeatureCard = styled(Card)(({ theme }) => ({
	height: "100%",
	display: "flex",
	flexDirection: "column",
	transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
	"&:hover": {
		transform: "translateY(-8px)",
		boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
	},
}));

const IconWrapper = styled(Box)(({ theme }) => ({
	width: "64px",
	height: "64px",
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 16px",
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepCard = styled(Paper)(({ theme }) => ({
	backgroundColor: "#ffffff",
	borderRadius: "20px",
	padding: "40px 30px",
	boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
	border: "2px solid rgba(0,0,0,0.05)",
	transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
	textAlign: "center",
	position: "relative",
	overflow: "hidden",
	height: "100%",
	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "4px",
		background: colors.mainSecondary,
		transform: "scaleX(0)",
		transition: "transform 0.4s ease",
	},
	"&:hover": {
		transform: "translateY(-10px)",
		boxShadow: "0 20px 40px rgba(30, 193, 149, 0.2)",
		borderColor: colors.mainSecondary,
		"&::before": {
			transform: "scaleX(1)",
		},
	},
	[theme.breakpoints.down("md")]: {
		padding: "30px 20px",
	},
}));

const StepNumber = styled(Typography)(({ theme }) => ({
	position: "absolute",
	top: "10px",
	right: "15px",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(48px, 6vw, 72px)",
	fontWeight: 900,
	color: "rgba(30, 193, 149, 0.08)",
	lineHeight: 1,
}));

const StepIconWrapper = styled(Box)(({ theme }) => ({
	width: "80px",
	height: "80px",
	borderRadius: "50%",
	backgroundColor: colors.mainSecondary,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 24px auto",
	color: "#ffffff",
	transition: "all 0.3s ease",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
	fontWeight: 700,
	color: colors.primary,
	marginBottom: "12px",
}));

const StepDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.9375rem",
	fontWeight: 400,
	color: "#6c757d",
	lineHeight: 1.7,
}));

const ConnectorLine = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	[theme.breakpoints.down("md")]: {
		display: "none",
	},
}));

const AnimatedArrow = styled(Box)(({ theme }) => ({
	color: colors.mainSecondary,
	animation: "slideRight 2s ease-in-out infinite",
	"@keyframes slideRight": {
		"0%, 100%": {
			transform: "translateX(0)",
		},
		"50%": {
			transform: "translateX(10px)",
		},
	},
}));

const CTAButton = styled(Button)(({ theme }) => ({
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	padding: "12px 32px",
	fontSize: "16px",
	fontWeight: 600,
	borderRadius: "8px",
	textTransform: "none",
	"&:hover": {
		backgroundColor: colors.mainSecondary,
		transform: "scale(1.05)",
	},
}));

const CommunitySupport: React.FC = () => {
	const navigate = useNavigate();
	const handleJoinClick = () => {
		navigate("/circle/sign-up");
	};

	const features = [
		{
			icon: <ForumIcon sx={{ fontSize: 40 }} />,
			title: "Active Discussion Forums",
			description:
				"Connect with members, ask questions, and share experiences. Get advice from people who've been where you are.",
		},
		{
			icon: <HandshakeIcon sx={{ fontSize: 40 }} />,
			title: "Peer-to-Peer Support",
			description:
				"Build relationships with others on similar financial journeys. Support and be supported by your community.",
		},
		{
			icon: <SchoolIcon sx={{ fontSize: 40 }} />,
			title: "Financial Education",
			description:
				"Access free workshops, webinars, and resources. Learn from experts and community members alike.",
		},
		{
			icon: <EmojiPeopleIcon sx={{ fontSize: 40 }} />,
			title: "Success Stories",
			description:
				"Get inspired by real stories from community members. See how others achieved their financial goals.",
		},
		{
			icon: <CelebrationIcon sx={{ fontSize: 40 }} />,
			title: "Community Events",
			description:
				"Join virtual and local meetups to network. Participate in challenges and celebrate milestones together.",
		},
		{
			icon: <VolunteerActivismIcon sx={{ fontSize: 40 }} />,
			title: "Give Back Programs",
			description:
				"Support others through mentorship and guidance. Help build a stronger, more connected community.",
		},
	];

	const howItWorksSteps = [
		{
			step: "01",
			icon: <PersonAdd sx={{ fontSize: 40 }} />,
			title: "Join the Community",
			description:
				"Create your profile and introduce yourself. Share your goals and what you hope to achieve.",
		},
		{
			step: "02",
			icon: <Groups sx={{ fontSize: 40 }} />,
			title: "Connect with Others",
			description:
				"Find people with similar goals and interests. Join groups, forums, and discussion threads.",
		},
		{
			step: "03",
			icon: <Share sx={{ fontSize: 40 }} />,
			title: "Share & Learn",
			description:
				"Ask questions, share tips, and learn from others. Contribute your own experiences to help the community.",
		},
		{
			step: "04",
			icon: <Stars sx={{ fontSize: 40 }} />,
			title: "Grow Together",
			description:
				"Celebrate wins, support during challenges, and grow stronger together. Build lasting friendships.",
		},
	];

	const benefits = [
		"Connect with thousands of like-minded savers and investors",
		"Get real-time support and advice from community members",
		"Access exclusive educational content and resources",
		"Participate in community challenges and competitions",
		"Build accountability partnerships for your financial goals",
		"Celebrate milestones with people who understand your journey",
	];

	return (
		<Box sx={{ bgcolor: "#f5f5f5" }}>
			{/* Hero Section */}
			<HeroSection>
				<Container maxWidth="lg">
					<Typography
						variant="h2"
						sx={{
							fontWeight: 700,
							mb: 2,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Community Support
					</Typography>
					<Typography
						variant="h5"
						sx={{
							mb: 4,
							opacity: 0.9,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Achieve More Together
					</Typography>
					<Typography
						variant="h6"
						sx={{
							maxWidth: "800px",
							margin: "0 auto",
							mb: 4,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Join a vibrant community of people working toward their financial
						goals. Share experiences, learn from others, and find the support
						you need to succeed. Because financial success is better achieved
						together.
					</Typography>
					<CTAButtonsWrapper
						data-aos="fade-up"
						data-aos-duration="1000"
						data-aos-delay="400">
						<PrimaryCTAButton
							variant="contained"
							size="large"
							onClick={handleJoinClick}>
							Join Our Community
						</PrimaryCTAButton>
					</CTAButtonsWrapper>
				</Container>
			</HeroSection>

			{/* What is Community Support */}
			<Container maxWidth="lg" sx={{ py: 8 }}>
				<Typography
					variant="h3"
					align="center"
					sx={{
						fontWeight: 800,
						mb: 2,
						color: colors.primary,
						fontFamily: "'Montserrat', sans-serif",
						fontSize: "clamp(2rem, 4vw, 3rem)",
						letterSpacing: "-0.02em",
					}}>
					The Power of Community
				</Typography>
				<Typography
					variant="body1"
					align="center"
					sx={{
						maxWidth: "900px",
						margin: "0 auto 6rem",
						fontSize: "clamp(1rem, 2vw, 1.25rem)",
						color: "#6c757d",
						fontFamily: "'Montserrat', sans-serif",
						lineHeight: 1.7,
					}}>
					Financial journeys can feel isolating, but they don't have to be. Our
					community brings together people from all walks of life who share a
					common goal: financial wellness. Whether you're saving for your first
					home, paying off debt, or building wealth, you'll find support,
					encouragement, and practical advice from people who understand exactly
					what you're going through.
				</Typography>

				{/* Features Grid */}
				<Grid container spacing={4} sx={{ mb: 8 }}>
					{features.map((feature, index) => (
						<Grid item xs={12} sm={6} md={4} key={index}>
							<FeatureCard>
								<CardContent>
									<IconWrapper>{feature.icon}</IconWrapper>
									<Typography
										variant="h6"
										sx={{ fontWeight: 600, mb: 2, color: "#333" }}>
										{feature.title}
									</Typography>
									<Typography
										variant="body2"
										sx={{ color: "#666", lineHeight: 1.7 }}>
										{feature.description}
									</Typography>
								</CardContent>
							</FeatureCard>
						</Grid>
					))}
				</Grid>
			</Container>

			{/* How It Works */}
			<Box sx={{ bgcolor: "#fff", py: 8 }}>
				<Container maxWidth="lg">
					<Typography
						variant="h3"
						align="center"
						sx={{
							fontWeight: 800,
							mb: 2,
							color: colors.primary,
							fontFamily: "'Montserrat', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
							letterSpacing: "-0.02em",
						}}>
						How It Works
					</Typography>
					<Typography
						variant="body1"
						align="center"
						sx={{
							maxWidth: "800px",
							margin: "0 auto 6rem",
							fontSize: "clamp(1rem, 2vw, 1.25rem)",
							color: "#6c757d",
							fontFamily: "'Montserrat', sans-serif",
							lineHeight: 1.7,
						}}>
						Become part of our supportive community in 4 simple steps
					</Typography>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								md: "1fr auto 1fr auto 1fr auto 1fr",
							},
							gap: { xs: "40px", md: "20px" },
							alignItems: "center",
						}}>
						{howItWorksSteps.map((item, index) => (
							<React.Fragment key={index}>
								<StepCard elevation={2}>
									<StepNumber>{item.step}</StepNumber>
									<StepIconWrapper>{item.icon}</StepIconWrapper>
									<StepTitle>{item.title}</StepTitle>
									<StepDescription>{item.description}</StepDescription>
								</StepCard>

								{index < howItWorksSteps.length - 1 && (
									<ConnectorLine>
										<AnimatedArrow>
											<ArrowForward sx={{ fontSize: 32 }} />
										</AnimatedArrow>
									</ConnectorLine>
								)}
							</React.Fragment>
						))}
					</Box>
				</Container>
			</Box>

			{/* Benefits Section */}
			<Container maxWidth="lg" sx={{ py: 8 }}>
				<Typography
					variant="h3"
					align="center"
					sx={{
						fontWeight: 800,
						mb: 2,
						color: colors.primary,
						fontFamily: "'Montserrat', sans-serif",
						fontSize: "clamp(2rem, 4vw, 3rem)",
						letterSpacing: "-0.02em",
					}}>
					Why Join Our Community?
				</Typography>
				<Typography
					variant="body1"
					align="center"
					sx={{
						maxWidth: "800px",
						margin: "0 auto 6rem",
						fontSize: "clamp(1rem, 2vw, 1.25rem)",
						color: "#6c757d",
						fontFamily: "'Montserrat', sans-serif",
						lineHeight: 1.7,
					}}>
					Experience the benefits of being part of something bigger
				</Typography>

				<Grid
					container
					spacing={3}
					sx={{ maxWidth: "900px", margin: "0 auto" }}>
					{benefits.map((benefit, index) => (
						<Grid item xs={12} sm={6} key={index}>
							<Box sx={{ display: "flex", alignItems: "flex-start" }}>
								<Box
									sx={{
										width: "24px",
										height: "24px",
										borderRadius: "50%",
										backgroundColor: colors.mainSecondary,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										color: "#fff",
										fontSize: "14px",
										fontWeight: 600,
										mr: 2,
										flexShrink: 0,
										mt: 0.5,
									}}>
									✓
								</Box>
								<Typography
									variant="body1"
									sx={{
										color: "#6c757d",
										lineHeight: 1.7,
										fontFamily: "'Montserrat', sans-serif",
									}}>
									{benefit}
								</Typography>
							</Box>
						</Grid>
					))}
				</Grid>
			</Container>

			{/* CTA Section */}
			<Box
				sx={{
					backgroundColor: colors.primary,
					color: "#fff",
					py: 8,
				}}>
				<Container maxWidth="md" sx={{ textAlign: "center" }}>
					<Typography
						variant="h3"
						sx={{
							fontWeight: 700,
							mb: 2,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Ready to Join Our Community?
					</Typography>
					<Typography
						variant="h6"
						sx={{
							mb: 4,
							opacity: 0.9,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Be part of a community that celebrates your wins and supports you
						through challenges
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}>
						<CTAButton
							onClick={() => navigate("/circle/sign-up")}
							sx={{
								bgcolor: "#fff",
								color: colors.primary,
								"&:hover": {
									bgcolor: "#fff",
								},
							}}>
							Join Now Free
						</CTAButton>
						<CTAButton
							onClick={() => navigate("/circle/sign-in")}
							sx={{
								bgcolor: "transparent",
								border: "2px solid #fff",
								"&:hover": {
									bgcolor: "rgba(255,255,255,0.1)",
								},
							}}>
							Sign In
						</CTAButton>
					</Box>
				</Container>
			</Box>
		</Box>
	);
};

export default CommunitySupport;
